import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { VStack } from 'tio-reimagined/components/layout';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import { formatCurrency } from 'tio-reimagined/format';
import Chip from 'tio-reimagined/components/chip';
import Button from 'tio-reimagined/components/button';
import StatCard from 'tio-reimagined/components/stat-card';
import PageHero from 'tio-reimagined/components/page-hero';
import Card from 'tio-reimagined/components/card';
import {
  PhMagnifyingGlass,
  PhGraduationCap,
  PhBuildings,
  PhFlag,
  PhBriefcase,
  PhArrowRight,
  PhCheckCircle,
  PhWarning,
  PhQuestion,
  PhLightning,
  PhCurrencyDollar,
  PhHandshake,
  PhBank,
  PhCaretRight,
} from 'ember-phosphor-icons';
import type { LrapDiscoverySummary, LrapProgram } from 'tio-employee/services/career-pathways-api';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';

interface LrapIndexModel {
  discovery: LrapDiscoverySummary;
  programs: LrapProgram[];
}

interface LrapIndexSignature {
  Args: {
    model: LrapIndexModel;
  };
}

export default class CareerPathwaysLrapIndex extends Component<LrapIndexSignature> {
  @service declare reimaginedData: ReimaginedDataService;

  @tracked activeTab: 'enrolled' | 'eligible' | 'all' = 'eligible';
  @tracked expandedProgramId: string | null = null;

  get discovery() {
    return this.args.model.discovery;
  }

  get programs() {
    return this.args.model.programs;
  }

  get hasCompletedDiscovery() {
    return this.discovery.hasCompletedDiscovery;
  }

  get enrolledPrograms() {
    return this.discovery.enrolledPrograms;
  }

  get eligiblePrograms() {
    return this.discovery.eligiblePrograms;
  }

  get hasOutsideFundsConflicts() {
    return this.discovery.outsideFundsConflicts.length > 0;
  }

  get stackingOptimization() {
    return this.discovery.stackingOptimization;
  }

  getTypeIcon = (type: string) => {
    switch (type) {
      case 'school':
        return PhGraduationCap;
      case 'state':
        return PhFlag;
      case 'federal':
        return PhBuildings;
      case 'employer':
        return PhBriefcase;
      default:
        return PhHandshake;
    }
  };

  getStatusColor = (status: string) => {
    switch (status) {
      case 'eligible':
        return 'success';
      case 'likely-eligible':
        return 'primary';
      case 'check-required':
        return 'warning';
      case 'ineligible':
        return 'danger';
      default:
        return 'default';
    }
  };

  toggleProgram = (programId: string) => {
    this.expandedProgramId = this.expandedProgramId === programId ? null : programId;
  };

  setTab = (tab: 'enrolled' | 'eligible' | 'all') => {
    this.activeTab = tab;
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <DataIndicator @source="placeholder" @showBadge={{true}} @dataSourceId="lrap-discovery">
      <VStack class="data-audit w-full min-w-0 max-w-6xl mx-auto">
        {{! Hero Section }}
        <PageHero @gradient="cool" @rounded="2xl">
          <:icon>
            <PhMagnifyingGlass @size={{32}} />
            <Heading @level={{1}} class="text-white md:text-3xl">
              LRAP Discovery
            </Heading>
          </:icon>
          <:default>
            <Text class="text-teal-100 text-lg mb-6 max-w-2xl">
              Discover loan repayment assistance programs from schools, states, and federal
              agencies. Find money you didn't know existed.
            </Text>

            {{#if this.hasCompletedDiscovery}}
              <div class="bg-white/10 backdrop-blur rounded-xl p-4 max-w-lg">
                <div class="flex items-center justify-between">
                  <div>
                    <Text class="text-teal-100 text-sm">Total Potential Annual Value</Text>
                    <Text class="text-2xl font-bold text-white">
                      {{formatCurrency this.discovery.totalPotentialAnnualValue}}
                    </Text>
                  </div>
                  <div class="text-right">
                    <Text class="text-teal-100 text-sm">Programs Found</Text>
                    <Text class="text-2xl font-bold text-white">
                      {{add this.enrolledPrograms.length this.eligiblePrograms.length}}
                    </Text>
                  </div>
                </div>
              </div>
            {{else}}
              <Button
                @intent="primary"
                @icon={{PhArrowRight}}
                @iconPlacement="end"
                @label="Start Discovery"
                class="bg-white tio-fg-info-body hover:tio-bg-info-soft-hover"
              />
            {{/if}}
          </:default>
        </PageHero>

        {{! Quick Stats }}
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <StatCard
            @icon={{PhGraduationCap}}
            @value="{{this.enrolledPrograms.length}}"
            @label="Enrolled Programs"
            @color="teal"
          />
          <StatCard
            @icon={{PhLightning}}
            @value="{{this.eligiblePrograms.length}}"
            @label="Eligible Programs"
            @trend="up"
            @trendText="Found for you"
            @color="violet"
          />
          <StatCard
            @icon={{PhCurrencyDollar}}
            @value={{formatCurrency this.discovery.totalPotentialAnnualValue}}
            @label="Potential Annual"
            @color="emerald"
          />
          <StatCard
            @icon={{PhWarning}}
            @value="{{this.discovery.outsideFundsConflicts.length}}"
            @label="Conflicts Found"
            @color="amber"
          />
        </div>

        {{! Outside Funds Conflicts Warning }}
        {{#if this.hasOutsideFundsConflicts}}
          <div class="mt-6 tio-bg-warning-soft border tio-border-warning rounded-xl p-4">
            <div class="tio-row-top">
              <PhWarning @size={{24}} class="tio-fg-warning shrink-0 mt-0.5" />
              <div>
                <Heading @level={{4}} class="tio-fg-warning-h mb-2">
                  "Outside Funds" Conflicts Detected
                </Heading>
                <Text class="tio-fg-warning-body text-sm mb-3">
                  Some of your programs may reduce benefits when you receive employer contributions.
                  We can help you optimize around these rules.
                </Text>
                {{#each this.discovery.outsideFundsConflicts as |conflict|}}
                  <div class="bg-white/60 rounded-lg p-3 mb-2">
                    <Text class="font-medium tio-fg-warning-h">{{conflict.schoolProgram}}</Text>
                    <Text class="tio-fg-warning-body text-sm">
                      {{conflict.recommendation}}
                    </Text>
                    <div class="tio-row-loose mt-2 text-sm">
                      <span class="tio-fg-secondary">
                        Employer contribution:
                        {{formatCurrency conflict.employerContribution}}
                      </span>
                      <span class="tio-fg-danger">
                        Potential reduction:
                        {{formatCurrency conflict.potentialReduction}}
                      </span>
                    </div>
                  </div>
                {{/each}}
                <LinkTo @route="authenticated.reimagined.finance.optimizer">
                  <Button
                    @intent="primary"
                    @size="sm"
                    @icon={{PhArrowRight}}
                    @iconPlacement="end"
                    @label="View Optimized Strategy"
                  />
                </LinkTo>
              </div>
            </div>
          </div>
        {{/if}}

        {{! Stacking Optimization Panel }}
        {{#if this.stackingOptimization}}
          <div
            class="mt-6 bg-gradient-to-r from-violet-50 to-purple-50 border tio-border-brand-soft rounded-xl p-6"
          >
            <div class="tio-row-tight mb-4">
              <PhLightning @size={{24}} class="tio-fg-brand" />
              <Heading @level={{3}} class="tio-fg-brand">
                Optimized Stacking Strategy
              </Heading>
            </div>

            <Text class="tio-fg-brand mb-4">{{this.stackingOptimization.reasoning}}</Text>

            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div class="bg-white/60 rounded-lg p-3">
                <Text @variant="muted" @size="sm">Monthly Out-of-Pocket</Text>
                <Text class="text-xl font-bold tio-fg-brand">
                  {{formatCurrency this.stackingOptimization.projectedOutcome.outOfPocketMonthly}}
                </Text>
              </div>
              <div class="bg-white/60 rounded-lg p-3">
                <Text @variant="muted" @size="sm">Years to Payoff</Text>
                <Text class="text-xl font-bold tio-fg-brand">
                  {{this.stackingOptimization.projectedOutcome.yearsToPayoff}}
                </Text>
              </div>
              <div class="bg-white/60 rounded-lg p-3">
                <Text @variant="muted" @size="sm">Total Forgiven</Text>
                <Text class="text-xl font-bold tio-fg-success">
                  {{formatCurrency this.stackingOptimization.projectedOutcome.totalForgiven}}
                </Text>
              </div>
              <div class="bg-white/60 rounded-lg p-3">
                <Text @variant="muted" @size="sm">Retirement Added</Text>
                <Text class="text-xl font-bold tio-fg-info">
                  {{formatCurrency
                    this.stackingOptimization.projectedOutcome.retirementContribution
                  }}
                </Text>
              </div>
            </div>

            <Heading @level={{4}} class="font-medium tio-fg-brand mb-2">Recommended Allocation:</Heading>
            <div class="space-y-2">
              {{#each this.stackingOptimization.optimizedAllocation as |allocation|}}
                <div class="bg-white rounded-lg p-3 tio-row-loose">
                  <div
                    class="w-10 h-10 rounded-full flex items-center justify-center
                      {{if
                        (isEqual allocation.sourceType 'school')
                        'tio-bg-brand-soft'
                        (if
                          (isEqual allocation.sourceType 'employer')
                          'tio-bg-info-soft'
                          (if
                            (isEqual allocation.sourceType 'retirement')
                            'tio-bg-success-soft'
                            'tio-bg-sunken'
                          )
                        )
                      }}"
                  >
                    {{#if (isEqual allocation.sourceType "school")}}
                      <PhGraduationCap @size={{20}} class="tio-fg-brand" />
                    {{else if (isEqual allocation.sourceType "employer")}}
                      <PhBriefcase @size={{20}} class="tio-fg-info" />
                    {{else if (isEqual allocation.sourceType "retirement")}}
                      <PhBank @size={{20}} class="tio-fg-success" />
                    {{else}}
                      <PhHandshake @size={{20}} class="tio-fg-secondary" />
                    {{/if}}
                  </div>
                  <div class="flex-1">
                    <div class="flex items-center justify-between">
                      <Text class="font-medium">{{allocation.source}}</Text>
                      <Text class="font-bold tio-fg-brand">
                        {{formatCurrency allocation.amount}}
                      </Text>
                    </div>
                    <Text @variant="muted" @size="sm">→ {{allocation.targetDebt}}</Text>
                  </div>
                </div>
              {{/each}}
            </div>
          </div>
        {{/if}}

        {{! Tab Navigation }}
        <div class="mt-8 border-b tio-border-default">
          <nav class="flex gap-6">
            <button
              type="button"
              class="pb-3 px-1 text-sm font-medium border-b-2 transition-colors
                {{if
                  (isEqual this.activeTab 'enrolled')
                  'tio-border-info tio-fg-info'
                  'border-transparent tio-fg-muted hover:tio-fg-secondary'
                }}"
              {{on "click" (fn this.setTab "enrolled")}}
            >
              Enrolled ({{this.enrolledPrograms.length}})
            </button>
            <button
              type="button"
              class="pb-3 px-1 text-sm font-medium border-b-2 transition-colors
                {{if
                  (isEqual this.activeTab 'eligible')
                  'tio-border-info tio-fg-info'
                  'border-transparent tio-fg-muted hover:tio-fg-secondary'
                }}"
              {{on "click" (fn this.setTab "eligible")}}
            >
              Eligible ({{this.eligiblePrograms.length}})
            </button>
            <button
              type="button"
              class="pb-3 px-1 text-sm font-medium border-b-2 transition-colors
                {{if
                  (isEqual this.activeTab 'all')
                  'tio-border-info tio-fg-info'
                  'border-transparent tio-fg-muted hover:tio-fg-secondary'
                }}"
              {{on "click" (fn this.setTab "all")}}
            >
              All Programs ({{this.programs.length}})
            </button>
          </nav>
        </div>

        {{! Program Lists }}
        <div class="mt-6 space-y-4">
          {{#if (isEqual this.activeTab "enrolled")}}
            {{#if this.enrolledPrograms.length}}
              {{#each this.enrolledPrograms as |enrollment|}}
                <Card @padding="sm" class="shadow-sm">
                  <div class="tio-row-top-loose">
                    <div class="p-3 tio-bg-success-soft rounded-xl">
                      {{#let (this.getTypeIcon enrollment.programType) as |Icon|}}
                        <Icon @size={{24}} class="tio-fg-success" />
                      {{/let}}
                    </div>
                    <div class="flex-1">
                      <div class="flex items-center justify-between">
                        <Text class="font-semibold">{{enrollment.programName}}</Text>
                        <Chip @intent="success" @size="sm">Active</Chip>
                      </div>
                      <div class="tio-row-loose mt-2 text-sm tio-fg-secondary">
                        <span>Annual: {{formatCurrency enrollment.annualBenefit}}</span>
                        <span>•</span>
                        <span>YTD: {{formatCurrency enrollment.ytdReceived}}</span>
                        <span>•</span>
                        <span>Lifetime: {{formatCurrency enrollment.lifetimeReceived}}</span>
                      </div>
                    </div>
                  </div>
                </Card>
              {{/each}}
            {{else}}
              <div class="text-center py-8">
                <PhGraduationCap @size={{48}} class="mx-auto tio-fg-disabled mb-4" />
                <Text class="tio-fg-secondary">No programs enrolled yet. Check your eligible
                  programs!</Text>
              </div>
            {{/if}}
          {{/if}}

          {{#if (isEqual this.activeTab "eligible")}}
            {{#if this.eligiblePrograms.length}}
              {{#each this.eligiblePrograms as |eligibility|}}
                <Card @padding="none" class="shadow-sm overflow-hidden">
                  <button
                    type="button"
                    class="w-full p-4 text-left hover:tio-bg-sunken transition-colors"
                    {{on "click" (fn this.toggleProgram eligibility.programId)}}
                  >
                    <div class="tio-row-top-loose">
                      <div class="p-3 tio-bg-brand-soft rounded-xl">
                        {{#let (this.getTypeIcon eligibility.program.type) as |Icon|}}
                          <Icon @size={{24}} class="tio-fg-brand" />
                        {{/let}}
                      </div>
                      <div class="flex-1">
                        <div class="flex items-center justify-between">
                          <Text class="font-semibold">{{eligibility.program.name}}</Text>
                          <div class="tio-row-tight">
                            <Chip @intent={{this.getStatusColor eligibility.status}} @size="sm">
                              {{eligibility.status}}
                            </Chip>
                            <Text class="font-bold tio-fg-success">
                              {{formatCurrency eligibility.estimatedAnnualValue}}/yr
                            </Text>
                          </div>
                        </div>
                        <Text @variant="muted" @size="sm" class="mt-1">
                          {{eligibility.program.description}}
                        </Text>
                      </div>
                      <PhCaretRight
                        @size={{20}}
                        class="tio-fg-disabled transition-transform
                          {{if (isEqual this.expandedProgramId eligibility.programId) 'rotate-90'}}"
                      />
                    </div>
                  </button>

                  {{#if (isEqual this.expandedProgramId eligibility.programId)}}
                    <div class="border-t tio-border-subtle p-4 tio-bg-sunken">
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Text class="font-medium tio-fg-secondary mb-2">Matched Criteria:</Text>
                          <ul class="space-y-1">
                            {{#each eligibility.matchedCriteria as |criteria|}}
                              <li class="tio-row-tight text-sm tio-fg-success-body">
                                <PhCheckCircle @size={{16}} />
                                {{criteria}}
                              </li>
                            {{/each}}
                          </ul>
                        </div>
                        {{#if eligibility.missingCriteria.length}}
                          <div>
                            <Text class="font-medium tio-fg-secondary mb-2">Missing Criteria:</Text>
                            <ul class="space-y-1">
                              {{#each eligibility.missingCriteria as |criteria|}}
                                <li class="tio-row-tight text-sm tio-fg-warning-body">
                                  <PhQuestion @size={{16}} />
                                  {{criteria}}
                                </li>
                              {{/each}}
                            </ul>
                          </div>
                        {{/if}}
                      </div>

                      {{#if eligibility.nextSteps.length}}
                        <div class="mt-4 pt-4 border-t tio-border-default">
                          <Text class="font-medium tio-fg-secondary mb-2">Next Steps:</Text>
                          <ul class="space-y-1">
                            {{#each eligibility.nextSteps as |step index|}}
                              <li class="tio-row-tight text-sm tio-fg-secondary">
                                <span
                                  class="w-5 h-5 rounded-full tio-bg-brand-soft tio-fg-brand text-xs flex items-center justify-center font-medium"
                                >
                                  {{add index 1}}
                                </span>
                                {{step}}
                              </li>
                            {{/each}}
                          </ul>
                        </div>
                      {{/if}}

                      <div class="mt-4 flex gap-2">
                        <a
                          href={{eligibility.program.applicationUrl}}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button
                            @intent="primary"
                            @size="sm"
                            @icon={{PhArrowRight}}
                            @iconPlacement="end"
                            @label="Apply Now"
                          />
                        </a>
                        <Button @intent="default" @size="sm" @appearance="outlined">
                          Save for Later
                        </Button>
                      </div>
                    </div>
                  {{/if}}
                </Card>
              {{/each}}
            {{else}}
              <div class="text-center py-8">
                <PhMagnifyingGlass @size={{48}} class="mx-auto tio-fg-disabled mb-4" />
                <Text class="tio-fg-secondary">Complete the discovery questionnaire to find eligible
                  programs.</Text>
                <Button @intent="primary" class="mt-4">
                  Start Discovery
                </Button>
              </div>
            {{/if}}
          {{/if}}

          {{#if (isEqual this.activeTab "all")}}
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              {{#each this.programs as |program|}}
                <Card @padding="sm" class="shadow-sm">
                  <div class="tio-row-top">
                    <div class="p-2 tio-bg-sunken rounded-lg">
                      {{#let (this.getTypeIcon program.type) as |Icon|}}
                        <Icon @size={{20}} class="tio-fg-secondary" />
                      {{/let}}
                    </div>
                    <div class="flex-1">
                      <Text class="font-medium">{{program.name}}</Text>
                      <Text @variant="muted" @size="sm">{{program.sponsor}}</Text>
                      <div class="tio-row-tight mt-2">
                        <Chip @intent="default" @size="sm">{{program.type}}</Chip>
                        <Text class="text-sm tio-fg-success font-medium">
                          Up to
                          {{formatCurrency program.maxAnnual}}/yr
                        </Text>
                      </div>
                    </div>
                  </div>
                </Card>
              {{/each}}
            </div>
          {{/if}}
        </div>

        {{! CTA }}
        <div class="mt-8 flex justify-center gap-4">
          <LinkTo @route="authenticated.reimagined.finance.optimizer">
            <Button @intent="default" @appearance="outlined" @label="View Section 127 Optimizer" />
          </LinkTo>
          <LinkTo @route="authenticated.reimagined.pslf-vault">
            <Button
              @intent="primary"
              @icon={{PhArrowRight}}
              @iconPlacement="end"
              @label="PSLF Compliance Vault"
            />
          </LinkTo>
        </div>
      </VStack>
    </DataIndicator>
  </template>
}

// Helper functions
function isEqual(a: unknown, b: unknown): boolean {
  return a === b;
}

function add(a: number, b: number): number {
  return a + b;
}
